import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import colors from "../../assets/css/colors.scss";
import styles from "./Alert.module.scss";
import Icon from "../Icon";

export const TYPES = ["error", "warning", "info", "success"];
export const TITLE_DISPLAYS = ["block", "inline"];
const ICONS = {
  error: <Icon icon="error" fill={colors.error} />,
  warning: <Icon icon="warning" fill={colors.warning} />,
  info: <Icon icon="info" fill={colors.info} />,
  success: <Icon icon="success" fill={colors.success} />
};

const Alert = ({ type, title, titleDisplay, fadeIn, message, closeIcon, show, onClose, className, showTitleIcon }) => {
  const [innerShow, setInnerShow] = useState(false);
  const [timeoutId, setTimeoutID] = useState(null);

  useEffect(() => {
    if (show && fadeIn) {
      setTimeoutID(
        setTimeout(() => {
          setInnerShow(false);
          onClose();
        }, fadeIn)
      );
    }
    setInnerShow(show);
  }, [fadeIn, show, onClose]);

  useEffect(() => {
    return () => {
      clearTimeout(timeoutId);
    };
  }, [timeoutId]);

  const onClick = useCallback(() => {
    setInnerShow(false);
    onClose();
    clearTimeout(timeoutId);
  }, [timeoutId, onClose]);

  return (
    <div className={cx(styles.container, styles[type], { [styles.hide]: !innerShow }, className)}>
      {showTitleIcon && <div className={cx(styles.icon, type)}>{ICONS[type]}</div>}
      <div className={styles.message}>
        {title && (
          <div style={{ display: titleDisplay }} className={styles.title}>
            {title}
          </div>
        )}
        <span>{message}</span>
      </div>
      {closeIcon && (
        <div className={styles.closeIcon}>
          <Icon icon="close" fill={colors.darkGray} onClick={onClick} size={12} />
        </div>
      )}
    </div>
  );
};

export default Alert;

Alert.defaultProps = {
  fadeIn: null,
  title: null,
  titleDisplay: "inline",
  className: null,
  show: false,
  closeIcon: true,
  showTitleIcon: true,
  onClose: () => {}
};

Alert.propTypes = {
  /** Alert message type. */
  type: PropTypes.oneOf(TYPES).isRequired,
  /** The amount of milliseconds for message to disappear. */
  fadeIn: PropTypes.number,
  /** The alert message title. */
  title: PropTypes.node,
  /** The title display. */
  titleDisplay: PropTypes.oneOf(TITLE_DISPLAYS),
  /** The Alert message. */
  message: PropTypes.node.isRequired,
  /** Toggle between showing close icon or not. */
  closeIcon: PropTypes.bool,
  /** Toggle between showing title icon or not. */
  showTitleIcon: PropTypes.bool,
  /** Callback function on close . */
  onClose: PropTypes.func,
  /** Toggle between showing alert message or not. */
  show: PropTypes.bool,
  /** ClassNames for overriding default styles. */
  className: PropTypes.string
};
